import type { InferOutput } from 'valibot';
// destruct to be sure that we don't ship whole valibot library to front
import {
  picklist as vPickList,
  object as vObject,
  optional as vOptional,
  array as vArray,
  string as vString,
  number as vNumber,
  literal as vLiteral,
} from 'valibot';
import { EpcGesSchema } from '@@/shared/property/epc-ges';
import { LocationCentroidSchema } from '@@/shared/location/types';
import { TransactionTypeSchema, FilterableFeatureSchema, ProjectTypeSchema } from '@@/shared/property/types';
import type { PropertyType } from '@@/shared/property/types';

export type SearchPropertyType = InferOutput<typeof SearchPropertyTypeSchema>;
export const SearchPropertyTypeSchema = vPickList([
  `apartment`,
  `house`,
  `parking`,
  `land`,
  `business`,
  `building`,
], `core.form.error.mandatory_field`);
// NOTE: Order is important as it is used in the UI and the URL priority
export const SEARCH_PROPERTY_TYPES = {
  APARTMENT: `apartment`,
  HOUSE: `house`,
  PARKING: `parking`,
  LAND: `land`,
  BUSINESS: `business`,
  BUILDING: `building`,
} satisfies Record<Uppercase<SearchPropertyType>, SearchPropertyType>;
export const SEARCH_PROPERTY_TYPES_VALUES = Object.values(SEARCH_PROPERTY_TYPES);

// That's the normalized params that we will use for:
// • Open Search query
// • Creation of page urls
export const SORT_OPTIONS = [`dateDesc`, `priceAsc`, `priceDesc`, `paidAtDesc`] as const;
export type SortOption = typeof SORT_OPTIONS[number];
export const LOCATION_RADIUS = [`5km`, `10km`, `15km`, `20km`, `30km`, `100km`] as const;
export const LocationRadiusSchema = vPickList(LOCATION_RADIUS);
export type LocationRadius = InferOutput<typeof LocationRadiusSchema>;
export const PropertySearchCriteriaSchema = vObject({
  transactionType: vOptional(TransactionTypeSchema),
  propertyTypes: vArray(SearchPropertyTypeSchema),
  propertyStatus: vOptional(vPickList([`active`, `sold`])),
  locations: vArray(vString()),
  coordinates: vOptional(LocationCentroidSchema),
  radius: vOptional(LocationRadiusSchema),
  rooms: vArray(vNumber()),
  features: vArray(FilterableFeatureSchema),
  surfaceMin: vOptional(vNumber()),
  surfaceMax: vOptional(vNumber()),
  surfaceVariation: vOptional(vLiteral(1.25)),
  priceMin: vOptional(vNumber()),
  priceMax: vOptional(vNumber()),
  priceVariation: vOptional(vLiteral(1.15)),
  epc: vArray(EpcGesSchema),
  propertyListingRef: vOptional(vString()),
  bedrooms: vArray(vNumber()),
  groundSurfaceMin: vOptional(vNumber()),
  groundSurfaceMax: vOptional(vNumber()),
  projectTypes: vArray(ProjectTypeSchema),
  page: vNumber(),
  itemsPerPage: vNumber(),
  sort: vOptional(vPickList(SORT_OPTIONS)),
  excludeRefs: vOptional(vArray(vString())),
  agentLocalIds: vOptional(vArray(vNumber())),
  transactionBuyerAgentLocalId: vOptional(vNumber()),
});
export type PropertySearchCriteria = InferOutput<typeof PropertySearchCriteriaSchema>;
export type PartialPropertySearchCriteria = Partial<PropertySearchCriteria>;
export type PropertySearchCriteriaName = Required<keyof PropertySearchCriteria>;
export type PropertySearchCriteriaNameList = Array<PropertySearchCriteriaName>;

export function getEmptySearchCriteria(): PropertySearchCriteria {
  return {
    page: 1,
    itemsPerPage: 30,
    locations: [],
    propertyTypes: [],
    rooms: [],
    features: [],
    epc: [],
    projectTypes: [],
    bedrooms: [],
  };
}

export function createPropertySearchCriteria(optionalSearchCriteria: PartialPropertySearchCriteria): PropertySearchCriteria {
  return {
    ...getEmptySearchCriteria(),
    ...optionalSearchCriteria,
  };
}

const PROPERTY_TYPE_TO_SEARCH_PROPERTY_TYPE: Record<PropertyType, SearchPropertyType> = {
  'apartment': 'apartment',
  'house': 'house',
  'parking': 'parking',
  'land': 'land',
  'building': 'building',
  'business-asset': 'business',
  'commercial-walls': 'business',
  'lease-hold': 'business',
};

/**
 * @description Maps a property type to its corresponding search property type.
 * @returns The search property type.
 */
export function propertyTypeToSearchPropertyType(propertyType: PropertyType): SearchPropertyType {
  return PROPERTY_TYPE_TO_SEARCH_PROPERTY_TYPE[propertyType];
}

/**
 * @description Maps a property type to its corresponding propertyTypes search criteria.
 */
export function propertyTypeToSearchCriteria(propertyType: PropertyType): [SearchPropertyType] {
  return [propertyTypeToSearchPropertyType(propertyType)];
}
